import { Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { OrderStatusOption, type Order } from "@fitness-app/data-models/entities/Order";

import { tagColorMapper } from "~/modules/Products/utils/tagColorMapper";

/**
 * The order status cell, shared by the global transactions list, the
 * per-product one and the client's history so the three never drift.
 *
 * A `FAILED` order that is still being retried is not the same thing as one
 * nobody will touch again, and the tag alone cannot tell them apart. Both
 * gateways know the difference — Stripe reports `next_payment_attempt`, and the
 * BLIK ladder is scheduled by us — so the date is spelled out under the tag
 * (TRE-2136).
 */
export const OrderStatusCell = ({ order }: { order: Order }) => {
  const { t } = useTranslation("products");

  const status = order.status || OrderStatusOption.Created;
  const nextAttemptAt =
    typeof order.metadata?.nextPaymentAttemptAt === "string" ? order.metadata.nextPaymentAttemptAt : null;
  const declineReason =
    typeof order.metadata?.blikDeclineMessage === "string" ? order.metadata.blikDeclineMessage : null;

  // Only an order the gateway is still trying to charge has a next attempt. A canceled one keeps the stale
  // `nextPaymentAttemptAt` from before the cancellation, so a deny-list here showed a retry that never comes.
  const stillCollecting = status === OrderStatusOption.Failed || status === OrderStatusOption.Created;

  return (
    <Space direction="vertical" size={2}>
      <Tag color={tagColorMapper[status]}>{t(`productTransactions.type.${status.toLowerCase()}`)}</Tag>
      {stillCollecting && declineReason ? (
        <Typography.Text type="secondary" className="whitespace-nowrap text-xs">
          {declineReason}
        </Typography.Text>
      ) : null}
      {stillCollecting && nextAttemptAt ? (
        <Typography.Text type="secondary" className="whitespace-nowrap text-xs">
          {t("productTransactions.nextAttempt", { date: dayjs(nextAttemptAt).format("DD.MM.YYYY HH:mm") })}
        </Typography.Text>
      ) : null}
    </Space>
  );
};
